// Enums
export enum UsageType {
  AUTOMATION_EXECUTED = 'automation_executed',
  BULLETIN_SENT = 'bulletin_sent',
  DSN_SENT = 'dsn_sent',
  EDITION_GENERATED = 'edition_generated',
  EDITION_VIEWED = 'edition_viewed',
}

export enum MonthlyBillingStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  PAID = 'paid',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

// Usage tracking
export interface FolderUsage {
  uuid: string;
  folderUuid: string;
  folderName: string;
  usageType: UsageType;
  month: number;
  year: number;
  timestamp: string;
  executionDetails?: Record<string, unknown>;
}

export interface ActiveFolder {
  uuid: string;
  name: string;
  usageCount: number;
  lastUsedAt: string;
  usageTypes: UsageType[];
}

export interface FolderUsageEvent {
  type: UsageType;
  timestamp: string;
  count: number;
}

// Billing Line Items
export interface BillingLineItem {
  uuid: string;
  folderUuid: string;
  folderName: string;
  usageCount: number;
  unitPriceHT: number;
  totalPriceHT: number;
  usageEvents: FolderUsageEvent[];
}

// Monthly Invoices
export interface MonthlyInvoice {
  uuid: string;
  month: number;
  year: number;
  uniqueFoldersCount: number;
  totalAmountHT: number;
  totalAmountTTC: number;
  vatAmount: number;
  vatRate: number;
  status: MonthlyBillingStatus;
  stripeInvoiceUrl: string | null;
  stripeInvoicePdfUrl: string | null;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  paidAt: string | null;
  createdAt: string;
}

export interface MonthlyInvoiceWithDetails extends MonthlyInvoice {
  lineItems: BillingLineItem[];
}

// Vault types
export interface VaultUsageInfo {
  activeVaultsCount: number;
  vaultActivationAmountHT: number;
  documentsUploadedCount: number;
  documentUploadAmountHT: number;
  totalVaultAmountHT: number;
}

export interface ActiveVault {
  uuid: string;
  name: string;
  folderUuid: string | null;
  folderName: string | null;
  activatedAt: string;
  unitPriceHT: number;
}

export interface UploadedDocument {
  uuid: string;
  vaultUuid: string;
  documentName: string;
  documentType: string;
  uploadedAt: string;
  unitPriceHT: number;
}

// API Responses
export interface CurrentMonthUsageResponse {
  month: number;
  year: number;
  uniqueFoldersCount: number;
  foldersAmountHT: number;
  estimatedAmountHT: number; // in centimes
  estimatedAmountTTC: number; // in centimes
  vatRate: number; // 20.0 for 20%
  folders: ActiveFolder[];
  vaultUsage: VaultUsageInfo & {
    activeVaults: ActiveVault[];
    uploadedDocuments: UploadedDocument[];
  };
}

export interface UsageHistoryEntry {
  month: number;
  year: number;
  uniqueFoldersCount: number;
  totalAmountHT: number;
  totalAmountTTC: number;
  status: 'current' | 'invoiced';
}

export interface UsageHistoryResponse {
  history: UsageHistoryEntry[];
}

export interface InvoicesListResponse {
  invoices: MonthlyInvoice[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface InvoiceDetailResponse extends MonthlyInvoice {
  lineItems: BillingLineItem[];
}

export interface BillingDashboardData {
  currentMonth: {
    month: number;
    year: number;
    uniqueFoldersCount: number;
    foldersAmountHT: number;
    estimatedAmountHT: number;
    estimatedAmountTTC: number;
    activeFolders: number;
    vaultUsage: VaultUsageInfo;
  };
  lastInvoice: MonthlyInvoice | null;
  nextInvoice: {
    estimatedDate: string;
    estimatedAmountTTC: number;
  };
  yearToDate: {
    totalAmountPaid: number;
    averageMonthlyAmount: number;
    totalFoldersUsed: number;
  };
}

export interface BillingDashboardResponse {
  data: BillingDashboardData;
}
